/**
 * Utility & Maintenance "Invisible Cost" Tracker - API layer
 * All mock data has been commented out. Now using real backend APIs.
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

// Shared API base URL (same as other modules)
const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Get auth token from Cognito via Amplify.
 */
const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (idToken) {
      const cleanedToken = idToken.trim();
      return cleanedToken;
    }

    console.warn('InvisibleCost API: No ID token found in session');
    return null;
  } catch (error) {
    console.error('InvisibleCost API: Error fetching auth session:', error);
    return null;
  }
};

/**
 * Generic helper to make authenticated API requests.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token.trim()}`,
  };

  const config = { method, headers };
  if (body) {
    config.body = JSON.stringify(body);
  }

  const url = `${API_BASE_URL}${endpoint}`;
  try {
    console.log(`InvisibleCost API: ${method} ${url}`);
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      let errorData = {};

      if (errorText) {
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText, error: errorText };
        }
      }

      console.error(
        `InvisibleCost API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const message =
        errorData.message ||
        errorData.error ||
        errorText ||
        'Unknown error from utilities API';

      const error = new Error(`API Error: ${response.status} - ${message}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_utility',
        `Utility API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[invisibleCostApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(
      `InvisibleCost API: Network or fetch error for ${endpoint}:`,
      error
    );
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_utility',
      `Utility API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[invisibleCostApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

// MOCK DATA - COMMENTED OUT (Using real API now)
// const MOCK_COSTS = [
//   {
//     cost_id: 'cost-001',
//     type: 'utility',
//     category: 'Electricity',
//     amount: 42500,
//     currency: 'INR',
//     site_id: 'site-kor-01',
//     site_name: 'Koramangala Hub',
//     period: '2025-01',
//     description: 'Monthly electricity bill - peak season',
//     created_at: '2025-01-28T10:00:00Z',
//     recorded_by: 'admin',
//   },
//   {
//     cost_id: 'cost-002',
//     type: 'utility',
//     category: 'Water',
//     amount: 8200,
//     currency: 'INR',
//     site_id: 'site-kor-01',
//     site_name: 'Koramangala Hub',
//     period: '2025-01',
//     description: 'Water supply and sewage',
//     created_at: '2025-01-25T14:00:00Z',
//     recorded_by: 'admin',
//   },
//   {
//     cost_id: 'cost-003',
//     type: 'maintenance',
//     category: 'HVAC',
//     amount: 15000,
//     currency: 'INR',
//     site_id: 'site-ind-01',
//     site_name: 'Indiranagar Outlet',
//     period: '2025-01',
//     description: 'AC servicing and filter replacement',
//     created_at: '2025-01-22T09:00:00Z',
//     recorded_by: 'admin',
//   },
//   {
//     cost_id: 'cost-004',
//     type: 'maintenance',
//     category: 'Plumbing',
//     amount: 6500,
//     currency: 'INR',
//     site_id: 'site-hsr-01',
//     site_name: 'HSR Layout Store',
//     period: '2025-01',
//     description: 'Leak repair and tap replacement',
//     created_at: '2025-01-20T11:00:00Z',
//     recorded_by: 'admin',
//   },
//   {
//     cost_id: 'cost-005',
//     type: 'utility',
//     category: 'Gas',
//     amount: 12000,
//     currency: 'INR',
//     site_id: 'site-jp-01',
//     site_name: 'JP Nagar Branch',
//     period: '2025-01',
//     description: 'LPG for kitchen and backup',
//     created_at: '2025-01-18T08:00:00Z',
//     recorded_by: 'admin',
//   },
//   {
//     cost_id: 'cost-006',
//     type: 'maintenance',
//     category: 'General',
//     amount: 23000,
//     currency: 'INR',
//     site_id: 'site-wht-01',
//     site_name: 'Whitefield Hub',
//     period: '2024-12',
//     description: 'Quarterly painting and minor repairs',
//     created_at: '2024-12-28T16:00:00Z',
//     recorded_by: 'admin',
//   },
//   {
//     cost_id: 'cost-007',
//     type: 'utility',
//     category: 'Electricity',
//     amount: 38900,
//     currency: 'INR',
//     site_id: 'site-ind-01',
//     site_name: 'Indiranagar Outlet',
//     period: '2025-01',
//     description: 'Monthly electricity',
//     created_at: '2025-01-27T10:00:00Z',
//     recorded_by: 'admin',
//   },
// ];

/**
 * Fetch all cost entries for the tenant.
 * @param {Object} options - { type?: 'utility'|'maintenance', siteId?: string, period?: string }
 * @returns {Promise<Array>}
 * @deprecated This function uses mock data. Replace with real API when available.
 */
export const getAllCosts = async (options = {}) => {
  // MOCK DATA - COMMENTED OUT (Using real API now)
  // await new Promise((r) => setTimeout(r, 400));
  // let list = [...MOCK_COSTS];
  // if (options.type) {
  //   list = list.filter((c) => c.type === options.type);
  // }
  // if (options.siteId) {
  //   list = list.filter((c) => c.site_id === options.siteId);
  // }
  // if (options.period) {
  //   list = list.filter((c) => c.period === options.period);
  // }
  // return list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  
  // Return empty array until real API is implemented
  return [];
};

/**
 * Get a single cost entry by id.
 * @param {string} costId
 * @returns {Promise<Object|null>}
 * @deprecated This function uses mock data. Replace with real API when available.
 */
export const getCostDetails = async (costId) => {
  // MOCK DATA - COMMENTED OUT (Using real API now)
  // await new Promise((r) => setTimeout(r, 200));
  // return MOCK_COSTS.find((c) => c.cost_id === costId) || null;
  
  // Return null until real API is implemented
  return null;
};

/**
 * Get aggregate stats for the invisible cost tracker.
 * @returns {Promise<{ totalAmount: number, utilityTotal: number, maintenanceTotal: number, entryCount: number }>}
 * @deprecated This function uses mock data. Replace with real API when available.
 */
export const getCostStats = async () => {
  // MOCK DATA - COMMENTED OUT (Using real API now)
  // await new Promise((r) => setTimeout(r, 150));
  // const utilityTotal = MOCK_COSTS.filter((c) => c.type === 'utility').reduce((s, c) => s + (c.amount || 0), 0);
  // const maintenanceTotal = MOCK_COSTS.filter((c) => c.type === 'maintenance').reduce((s, c) => s + (c.amount || 0), 0);
  // return {
  //   totalAmount: utilityTotal + maintenanceTotal,
  //   utilityTotal,
  //   maintenanceTotal,
  //   entryCount: MOCK_COSTS.length,
  // };
  
  // Return empty stats until real API is implemented
  return {
    totalAmount: 0,
    utilityTotal: 0,
    maintenanceTotal: 0,
    entryCount: 0,
  };
};

/**
 * Get costs grouped by category (for charts/summary).
 * @returns {Promise<Array<{ category: string, amount: number, type: string }>>}
 * @deprecated This function uses mock data. Replace with real API when available.
 */
export const getCostsByCategory = async () => {
  // MOCK DATA - COMMENTED OUT (Using real API now)
  // await new Promise((r) => setTimeout(r, 200));
  // const map = {};
  // MOCK_COSTS.forEach((c) => {
  //   const key = c.category || 'Other';
  //   if (!map[key]) map[key] = { category: key, amount: 0, type: c.type };
  //   map[key].amount += c.amount || 0;
  // });
  // return Object.values(map).sort((a, b) => b.amount - a.amount);
  
  // Return empty array until real API is implemented
  return [];
};

// MOCK DATA - COMMENTED OUT (Using real API now)
// Mock utility logs data
// const MOCK_UTILITY_LOGS = [
//   {
//     log_id: 'log-001',
//     utility_type: 'Electricity',
//     meter_reading_start: 12500,
//     meter_reading_end: 12850,
//     usage: 350,
//     invoice_amount: 42500,
//     cost_per_unit: 121.43,
//     period: '2025-01',
//     site_id: 'site-kor-01',
//     site_name: 'Koramangala Hub',
//     appliance_consumption_trend: 'stable',
//     leakage_alert_status: 'none',
//     guest_hours: 1250,
//     cost_per_guest_hour: 34.0,
//     created_at: '2025-01-28T10:00:00Z',
//   },
//   {
//     log_id: 'log-002',
//     utility_type: 'Water',
//     meter_reading_start: 8500,
//     meter_reading_end: 8750,
//     usage: 250,
//     invoice_amount: 8200,
//     cost_per_unit: 32.8,
//     period: '2025-01',
//     site_id: 'site-kor-01',
//     site_name: 'Koramangala Hub',
//     appliance_consumption_trend: 'increasing',
//     leakage_alert_status: 'warning',
//     guest_hours: 1250,
//     cost_per_guest_hour: 6.56,
//     created_at: '2025-01-25T14:00:00Z',
//   },
//   {
//     log_id: 'log-003',
//     utility_type: 'Gas',
//     meter_reading_start: 1200,
//     meter_reading_end: 1350,
//     usage: 150,
//     invoice_amount: 12000,
//     cost_per_unit: 80.0,
//     period: '2025-01',
//     site_id: 'site-jp-01',
//     site_name: 'JP Nagar Branch',
//     appliance_consumption_trend: 'stable',
//     leakage_alert_status: 'none',
//     guest_hours: 980,
//     cost_per_guest_hour: 12.24,
//     created_at: '2025-01-18T08:00:00Z',
//   },
// ];

// MOCK DATA - COMMENTED OUT (Using real API now)
// Mock alerts data
// const MOCK_ALERTS = [
//   {
//     alert_id: 'alert-001',
//     alert_type: 'spike',
//     severity: 'high',
//     utility_type: 'Electricity',
//     site_id: 'site-ind-01',
//     site_name: 'Indiranagar Outlet',
//     appliance_id: 'app-001',
//     appliance_name: 'HVAC System - Main Hall',
//     spike_percentage: 45,
//     threshold: 30,
//     current_usage: 450,
//     previous_usage: 310,
//     period: '2025-01',
//     status: 'open',
//     maintenance_ticket_id: null,
//     created_at: '2025-01-27T10:00:00Z',
//     timeline: [
//       { status: 'detected', timestamp: '2025-01-27T10:00:00Z', user: 'System' },
//     ],
//   },
//   {
//     alert_id: 'alert-002',
//     alert_type: 'leakage',
//     severity: 'medium',
//     utility_type: 'Water',
//     site_id: 'site-kor-01',
//     site_name: 'Koramangala Hub',
//     appliance_id: 'app-002',
//     appliance_name: 'Water Heater - Kitchen',
//     spike_percentage: 25,
//     threshold: 20,
//     current_usage: 250,
//     previous_usage: 200,
//     period: '2025-01',
//     status: 'acknowledged',
//     maintenance_ticket_id: 'ticket-001',
//     created_at: '2025-01-25T14:00:00Z',
//     timeline: [
//       { status: 'detected', timestamp: '2025-01-25T14:00:00Z', user: 'System' },
//       { status: 'acknowledged', timestamp: '2025-01-26T09:00:00Z', user: 'Admin User' },
//     ],
//   },
// ];

/**
 * Get utility matrix dashboard data
 * @param {Object} options - { siteId?: string, period?: string }
 * @returns {Promise<Object>}
 * @deprecated This function uses mock data. Use listUtilityLogs() instead (already integrated in UtilityMatrixDashboard.js).
 */
export const getUtilityMatrixDashboard = async (options = {}) => {
  // MOCK DATA - COMMENTED OUT (Using real API now - see UtilityMatrixDashboard.js)
  // await new Promise((r) => setTimeout(r, 400));
  // 
  // let logs = [...MOCK_UTILITY_LOGS];
  // if (options.siteId) {
  //   logs = logs.filter((l) => l.site_id === options.siteId);
  // }
  // if (options.period) {
  //   logs = logs.filter((l) => l.period === options.period);
  // }
  //
  // // Calculate summary stats
  // const totalSpend = logs.reduce((sum, l) => sum + (l.invoice_amount || 0), 0);
  // const totalGuestHours = logs.reduce((sum, l) => sum + (l.guest_hours || 0), 0);
  // const spendPerGuestHour = totalGuestHours > 0 ? totalSpend / totalGuestHours : 0;
  //
  // // Find highest consuming appliance
  // const applianceMap = {};
  // logs.forEach((l) => {
  //   const key = l.appliance_id || 'unknown';
  //   if (!applianceMap[key]) {
  //     applianceMap[key] = {
  //       appliance_id: l.appliance_id,
  //       appliance_name: l.appliance_name || 'Unknown',
  //       total_usage: 0,
  //       total_cost: 0,
  //     };
  //   }
  //   applianceMap[key].total_usage += l.usage || 0;
  //   applianceMap[key].total_cost += l.invoice_amount || 0;
  // });
  // const highestConsumingAppliance = Object.values(applianceMap).sort(
  //   (a, b) => b.total_cost - a.total_cost
  // )[0] || null;
  //
  // // Calculate month-over-month change (mock)
  // const previousPeriod = options.period === '2025-01' ? '2024-12' : '2025-01';
  // const previousLogs = MOCK_UTILITY_LOGS.filter((l) => l.period === previousPeriod);
  // const previousSpend = previousLogs.reduce((sum, l) => sum + (l.invoice_amount || 0), 0);
  // const momChange = previousSpend > 0 ? ((totalSpend - previousSpend) / previousSpend) * 100 : 0;
  //
  // // Group by utility type for chart
  // const utilityTypeMap = {};
  // logs.forEach((l) => {
  //   const key = l.utility_type || 'Other';
  //   if (!utilityTypeMap[key]) {
  //     utilityTypeMap[key] = {
  //       utility_type: key,
  //       total_cost: 0,
  //       total_usage: 0,
  //       cost_per_guest_hour: 0,
  //       logs: [],
  //     };
  //   }
  //   utilityTypeMap[key].total_cost += l.invoice_amount || 0;
  //   utilityTypeMap[key].total_usage += l.usage || 0;
  //   utilityTypeMap[key].logs.push(l);
  // });
  //
  // // Calculate cost per guest hour for each utility type
  // Object.values(utilityTypeMap).forEach((ut) => {
  //   const totalGH = ut.logs.reduce((sum, l) => sum + (l.guest_hours || 0), 0);
  //   ut.cost_per_guest_hour = totalGH > 0 ? ut.total_cost / totalGH : 0;
  // });
  //
  // // Get insights (spikes and suggestions)
  // const insights = logs
  //   .filter((l) => l.leakage_alert_status !== 'none' || l.appliance_consumption_trend === 'increasing')
  //   .map((l) => ({
  //     type: l.leakage_alert_status !== 'none' ? 'leakage' : 'spike',
  //     utility_type: l.utility_type,
  //     site_name: l.site_name,
  //     message: l.leakage_alert_status !== 'none'
  //       ? `Potential leakage detected in ${l.utility_type} at ${l.site_name}`
  //       : `Increasing consumption trend in ${l.utility_type} at ${l.site_name}`,
  //     severity: l.leakage_alert_status === 'critical' ? 'high' : 'medium',
  //   }));
  //
  // return {
  //   summary: {
  //     totalSpend,
  //     spendPerGuestHour,
  //     highestConsumingAppliance,
  //     monthOverMonthChange: momChange,
  //   },
  //   utilityMatrix: Object.values(utilityTypeMap),
  //   chartData: {
  //     labels: Object.keys(utilityTypeMap),
  //     datasets: [
  //       {
  //         label: 'Cost (₹)',
  //         data: Object.values(utilityTypeMap).map((ut) => ut.total_cost),
  //         backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'],
  //       },
  //     ],
  //   },
  //   insights,
  // };
  
  // Return empty dashboard data until real API is implemented
  // Note: UtilityMatrixDashboard.js now uses listUtilityLogs() instead
  return {
    summary: {
      totalSpend: 0,
      spendPerGuestHour: 0,
      highestConsumingAppliance: null,
      monthOverMonthChange: 0,
    },
    utilityMatrix: [],
    chartData: {
      labels: [],
      datasets: [{ label: 'Cost (₹)', data: [], backgroundColor: [] }],
    },
    insights: [],
  };
};

/**
 * Normalize utility_type from UI to API-accepted values.
 * API expects: electricity | water | gas | lpg
 */
const normalizeUtilityType = (type) => {
  if (!type) return '';
  const t = type.toString().trim().toLowerCase();
  if (t.startsWith('elect')) return 'electricity';
  if (t.startsWith('wat')) return 'water';
  if (t === 'gas' || t.startsWith('gas')) return 'gas';
  if (t === 'lpg') return 'lpg';
  return t;
};

/**
 * Create a new utility log entry (real backend)
 * POST /utilities/logs
 * @param {Object} logData - Utility log data from UI
 * @param {string} logData.site_id - Site ID (required)
 * @param {string} logData.utility_type - Utility type: electricity|water|gas|lpg (required)
 * @param {number} logData.meter_reading_start - Starting meter reading (required)
 * @param {number} logData.meter_reading_end - Ending meter reading (required)
 * @param {number} logData.invoice_amount - Invoice amount (required)
 * @param {string} logData.period - Period in format YYYY-MM (required)
 * @param {Object} [logData.appliance_consumption_trend] - Appliance consumption trend object (optional)
 * @param {string} [logData.leakage_alert_status] - Leakage alert status: none|suspected|confirmed (optional, default: 'none')
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const createUtilityLog = async (logData) => {
  try {
    // Validate required fields
    if (!logData.site_id) {
      throw new Error('site_id is required');
    }
    if (!logData.utility_type) {
      throw new Error('utility_type is required');
    }
    if (logData.meter_reading_start === undefined || logData.meter_reading_start === null) {
      throw new Error('meter_reading_start is required');
    }
    if (logData.meter_reading_end === undefined || logData.meter_reading_end === null) {
      throw new Error('meter_reading_end is required');
    }
    if (logData.invoice_amount === undefined || logData.invoice_amount === null) {
      throw new Error('invoice_amount is required');
    }
    if (!logData.period) {
      throw new Error('period is required');
    }

    const payload = {
      site_id: logData.site_id,
      utility_type: normalizeUtilityType(logData.utility_type),
      meter_reading_start: logData.meter_reading_start,
      meter_reading_end: logData.meter_reading_end,
      invoice_amount: logData.invoice_amount,
      period: logData.period,
    };

    // Add optional fields if provided
    if (logData.appliance_consumption_trend) {
      payload.appliance_consumption_trend = logData.appliance_consumption_trend;
    }
    if (logData.leakage_alert_status) {
      payload.leakage_alert_status = logData.leakage_alert_status;
    } else {
      payload.leakage_alert_status = 'none';
    }

    const response = await makeApiRequest('/utilities/logs', 'POST', payload);
    const data = await response.json();

    // Log API event for creating utility log
    loggingService.logInfo(
      'api_success_utility',
      `Successfully created utility log`,
      {
        endpoint: '/utilities/logs',
        method: 'POST',
        siteId: logData.site_id,
        utilityType: payload.utility_type,
        period: logData.period,
      }
    ).catch(logError => {
      console.warn('[invisibleCostApi] Failed to log success event:', logError);
    });

    return {
      success: true,
      data,
      utility_log: data,
    };
  } catch (error) {
    console.error('InvisibleCost API: Error creating utility log:', error);
    return {
      success: false,
      error: error.message || 'Failed to create utility log',
      status: error.status,
      responseData: error.responseData,
    };
  }
};

/**
 * List utility logs
 * GET /utilities/logs?site_id={site_id}&period={period}
 * @param {Object} options - Query parameters
 * @param {string} [options.siteId] - Filter by site ID
 * @param {string} [options.period] - Filter by period (format: YYYY-MM)
 * @returns {Promise<{success: boolean, data?: any, logs?: Array, error?: string}>}
 */
export const listUtilityLogs = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.siteId) {
      params.append('site_id', options.siteId);
    }
    if (options.period) {
      params.append('period', options.period);
    }

    const endpoint = `/utilities/logs${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await makeApiRequest(endpoint, 'GET');
    const data = await response.json();
    const logs = Array.isArray(data) ? data : data.logs || data.utility_logs || [];

    // Log API event for listing utility logs
    loggingService.logInfo(
      'api_success_utility',
      `Successfully listed utility logs`,
      {
        endpoint: '/utilities/logs',
        method: 'GET',
        siteId: options.siteId,
        period: options.period,
        logsCount: logs.length,
      }
    ).catch(logError => {
      console.warn('[invisibleCostApi] Failed to log success event:', logError);
    });

    return {
      success: true,
      data,
      logs,
    };
  } catch (error) {
    console.error('InvisibleCost API: Error listing utility logs:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch utility logs',
      status: error.status,
      responseData: error.responseData,
      logs: [],
    };
  }
};

/**
 * Get a single utility log
 * GET /utilities/logs/{utility_log_id}
 * @param {string} utilityLogId - Utility log ID
 * @returns {Promise<{success: boolean, data?: any, utility_log?: any, error?: string}>}
 */
export const getUtilityLog = async (utilityLogId) => {
  try {
    if (!utilityLogId) {
      throw new Error('Utility log ID is required');
    }

    const response = await makeApiRequest(`/utilities/logs/${utilityLogId}`, 'GET');
    const data = await response.json();

    // Log API event for getting utility log
    loggingService.logInfo(
      'api_success_utility',
      `Successfully fetched utility log ${utilityLogId}`,
      {
        endpoint: `/utilities/logs/${utilityLogId}`,
        method: 'GET',
        utilityLogId,
      }
    ).catch(logError => {
      console.warn('[invisibleCostApi] Failed to log success event:', logError);
    });

    return {
      success: true,
      data,
      utility_log: data,
    };
  } catch (error) {
    console.error('InvisibleCost API: Error fetching utility log:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch utility log',
      status: error.status,
      responseData: error.responseData,
    };
  }
};

/**
 * Update a utility log
 * PUT /utilities/logs/{utility_log_id}
 * @param {string} utilityLogId - Utility log ID
 * @param {Object} updates - Fields to update (partial update supported)
 * @returns {Promise<{success: boolean, data?: any, utility_log?: any, error?: string}>}
 */
export const updateUtilityLog = async (utilityLogId, updates) => {
  try {
    if (!utilityLogId) {
      throw new Error('Utility log ID is required');
    }

    const payload = { ...updates };
    
    // Normalize utility_type if provided
    if (payload.utility_type) {
      payload.utility_type = normalizeUtilityType(payload.utility_type);
    }

    const response = await makeApiRequest(
      `/utilities/logs/${utilityLogId}`,
      'PUT',
      payload
    );
    const data = await response.json();

    // Log API event for updating utility log
    loggingService.logInfo(
      'api_success_utility',
      `Successfully updated utility log ${utilityLogId}`,
      {
        endpoint: `/utilities/logs/${utilityLogId}`,
        method: 'PUT',
        utilityLogId,
        updatedFields: Object.keys(updates || {}),
      }
    ).catch(logError => {
      console.warn('[invisibleCostApi] Failed to log success event:', logError);
    });

    return {
      success: true,
      data,
      utility_log: data,
    };
  } catch (error) {
    console.error('InvisibleCost API: Error updating utility log:', error);
    return {
      success: false,
      error: error.message || 'Failed to update utility log',
      status: error.status,
      responseData: error.responseData,
    };
  }
};

/**
 * Delete a utility log
 * DELETE /utilities/logs/{utility_log_id}
 * @param {string} utilityLogId - Utility log ID
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const deleteUtilityLog = async (utilityLogId) => {
  try {
    if (!utilityLogId) {
      throw new Error('Utility log ID is required');
    }

    const response = await makeApiRequest(
      `/utilities/logs/${utilityLogId}`,
      'DELETE'
    );
    const data = await response.json().catch(() => ({}));

    // Log API event for deleting utility log
    loggingService.logInfo(
      'api_success_utility',
      `Successfully deleted utility log ${utilityLogId}`,
      {
        endpoint: `/utilities/logs/${utilityLogId}`,
        method: 'DELETE',
        utilityLogId,
      }
    ).catch(logError => {
      console.warn('[invisibleCostApi] Failed to log success event:', logError);
    });

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('InvisibleCost API: Error deleting utility log:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete utility log',
      status: error.status,
      responseData: error.responseData,
    };
  }
};

/**
 * Create a new utility alert
 * POST /utilities/alerts
 * @param {Object} alertData - Utility alert data
 * @param {string} alertData.site_id - Site ID (required)
 * @param {string} alertData.utility_type - Utility type: water|electricity|gas (required)
 * @param {string} alertData.severity - Severity: low|medium|high|critical (required)
 * @param {string} alertData.status - Status: open|acknowledged|resolved (required)
 * @param {number} alertData.threshold_value - Threshold value (required)
 * @param {number} alertData.current_value - Current value (required)
 * @param {string} [alertData.appliance_id] - Appliance ID (optional)
 * @param {string} [alertData.notes] - Notes (optional)
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const createUtilityAlert = async (alertData) => {
  try {
    // Validate required fields
    if (!alertData.site_id) {
      throw new Error('site_id is required');
    }
    if (!alertData.utility_type) {
      throw new Error('utility_type is required');
    }
    if (!alertData.severity) {
      throw new Error('severity is required');
    }
    if (!alertData.status) {
      throw new Error('status is required');
    }
    if (alertData.threshold_value === undefined || alertData.threshold_value === null) {
      throw new Error('threshold_value is required');
    }
    if (alertData.current_value === undefined || alertData.current_value === null) {
      throw new Error('current_value is required');
    }

    // Normalize utility type
    const normalizedUtilityType = normalizeUtilityType(alertData.utility_type);

    const payload = {
      site_id: alertData.site_id,
      utility_type: normalizedUtilityType,
      severity: alertData.severity,
      status: alertData.status,
      threshold_value: alertData.threshold_value,
      current_value: alertData.current_value,
    };

    // Add optional fields if provided
    if (alertData.appliance_id) {
      payload.appliance_id = alertData.appliance_id;
    }
    if (alertData.notes) {
      payload.notes = alertData.notes;
    }

    const response = await makeApiRequest('/utilities/alerts', 'POST', payload);
    const data = await response.json();

    // Log API event for creating utility alert
    loggingService.logInfo(
      'api_success_utility',
      `Successfully created utility alert`,
      {
        endpoint: '/utilities/alerts',
        method: 'POST',
        siteId: alertData.site_id,
        utilityType: normalizedUtilityType,
        severity: alertData.severity,
      }
    ).catch(logError => {
      console.warn('[invisibleCostApi] Failed to log success event:', logError);
    });

    return {
      success: true,
      data,
      alert: data,
    };
  } catch (error) {
    console.error('InvisibleCost API: Error creating utility alert:', error);
    return {
      success: false,
      error: error.message || 'Failed to create utility alert',
      status: error.status,
      responseData: error.responseData,
    };
  }
};

/**
 * List utility alerts
 * GET /utilities/alerts?site_id={site_id}&status={status}
 * @param {Object} options - Query parameters
 * @param {string} [options.siteId] - Filter by site ID
 * @param {string} [options.status] - Filter by status: open|acknowledged|resolved
 * @returns {Promise<{success: boolean, alerts?: Array, error?: string}>}
 */
export const listUtilityAlerts = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.siteId) {
      params.append('site_id', options.siteId);
    }
    if (options.status) {
      params.append('status', options.status);
    }

    const endpoint = `/utilities/alerts${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await makeApiRequest(endpoint, 'GET');
    const data = await response.json();
    const alerts = Array.isArray(data) ? data : data.alerts || data.items || [];

    // Log API event for listing utility alerts
    loggingService.logInfo(
      'api_success_utility',
      `Successfully listed utility alerts`,
      {
        endpoint: '/utilities/alerts',
        method: 'GET',
        siteId: options.siteId,
        status: options.status,
        alertsCount: alerts.length,
      }
    ).catch(logError => {
      console.warn('[invisibleCostApi] Failed to log success event:', logError);
    });

    return {
      success: true,
      data,
      alerts,
    };
  } catch (error) {
    console.error('InvisibleCost API: Error listing utility alerts:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch utility alerts',
      status: error.status,
      responseData: error.responseData,
      alerts: [],
    };
  }
};

/**
 * Get a single utility alert
 * GET /utilities/alerts/{alert_id}
 * @param {string} alertId - Alert ID
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const getUtilityAlert = async (alertId) => {
  try {
    if (!alertId) {
      throw new Error('Alert ID is required');
    }

    const response = await makeApiRequest(`/utilities/alerts/${alertId}`, 'GET');
    const data = await response.json();

    // Log API event for getting utility alert
    loggingService.logInfo(
      'api_success_utility',
      `Successfully fetched utility alert ${alertId}`,
      {
        endpoint: `/utilities/alerts/${alertId}`,
        method: 'GET',
        alertId,
      }
    ).catch(logError => {
      console.warn('[invisibleCostApi] Failed to log success event:', logError);
    });

    return {
      success: true,
      data,
      alert: data,
    };
  } catch (error) {
    console.error('InvisibleCost API: Error fetching utility alert:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch utility alert',
      status: error.status,
      responseData: error.responseData,
    };
  }
};

/**
 * Update a utility alert
 * PUT /utilities/alerts/{alert_id}
 * @param {string} alertId - Alert ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const updateUtilityAlert = async (alertId, updates) => {
  try {
    if (!alertId) {
      throw new Error('Alert ID is required');
    }

    const payload = { ...updates };

    // Normalize utility_type if provided
    if (payload.utility_type) {
      payload.utility_type = normalizeUtilityType(payload.utility_type);
    }

    const response = await makeApiRequest(
      `/utilities/alerts/${alertId}`,
      'PUT',
      payload
    );
    const data = await response.json();

    // Log API event for updating utility alert
    loggingService.logInfo(
      'api_success_utility',
      `Successfully updated utility alert ${alertId}`,
      {
        endpoint: `/utilities/alerts/${alertId}`,
        method: 'PUT',
        alertId,
        updatedFields: Object.keys(updates || {}),
      }
    ).catch(logError => {
      console.warn('[invisibleCostApi] Failed to log success event:', logError);
    });

    return {
      success: true,
      data,
      alert: data,
    };
  } catch (error) {
    console.error('InvisibleCost API: Error updating utility alert:', error);
    return {
      success: false,
      error: error.message || 'Failed to update utility alert',
      status: error.status,
      responseData: error.responseData,
    };
  }
};

/**
 * Delete a utility alert
 * DELETE /utilities/alerts/{alert_id}
 * @param {string} alertId - Alert ID
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const deleteUtilityAlert = async (alertId) => {
  try {
    if (!alertId) {
      throw new Error('Alert ID is required');
    }

    const response = await makeApiRequest(
      `/utilities/alerts/${alertId}`,
      'DELETE'
    );
    const data = await response.json().catch(() => ({}));

    // Log API event for deleting utility alert
    loggingService.logInfo(
      'api_success_utility',
      `Successfully deleted utility alert ${alertId}`,
      {
        endpoint: `/utilities/alerts/${alertId}`,
        method: 'DELETE',
        alertId,
      }
    ).catch(logError => {
      console.warn('[invisibleCostApi] Failed to log success event:', logError);
    });

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('InvisibleCost API: Error deleting utility alert:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete utility alert',
      status: error.status,
      responseData: error.responseData,
    };
  }
};

/**
 * Get utility alerts (legacy function - kept for backward compatibility)
 * @deprecated Use listUtilityAlerts instead
 * @param {Object} options - { siteId?: string, status?: string }
 * @returns {Promise<Array>}
 */
export const getUtilityAlerts = async (options = {}) => {
  const result = await listUtilityAlerts(options);
  if (result.success) {
    return result.alerts || [];
  }
  return [];
};

/**
 * Update alert status (legacy function - kept for backward compatibility)
 * @deprecated Use updateUtilityAlert instead
 * @param {string} alertId - Alert ID
 * @param {string} action - 'acknowledge' | 'resolve' | 'open_ticket'
 * @param {Object} data - Additional data (e.g., ticket_id)
 * @returns {Promise<Object>}
 */
export const updateAlertStatus = async (alertId, action, data = {}) => {
  const statusMap = {
    acknowledge: 'acknowledged',
    resolve: 'resolved',
    open_ticket: 'acknowledged',
  };

  const updates = {
    status: statusMap[action] || action,
  };

  if (action === 'open_ticket' && data.ticket_id) {
    // Note: This assumes the API supports a maintenance_ticket_id field
    // Adjust based on actual API structure
    updates.maintenance_ticket_id = data.ticket_id;
  }

  return await updateUtilityAlert(alertId, updates);
};
